
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Surgical Teams Service
 * Uses backend API endpoints instead of direct Firestore access
 */
export const surgicalTeamsService = {
  /**
   * Get all surgical teams for a facility
   * @param {string} facilityId - Facility ID
   * @param {Object} options - Additional options (status, specialty, page, limit)
   * @returns {Promise<Array>} Promise resolving to array of teams
   */
  getAllTeams: async (facilityId, options = {}) => {
    try {
      console.log('Fetching surgical teams for facility via API:', facilityId);
      const token = await getAuthToken();
      const { status, specialty, page = 1, limit = 50 } = options;
      
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (specialty) params.append('specialty', specialty);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Fetched surgical teams:', response.data.teams);
      return response.data.teams;
    } catch (error) {
      console.error('Error fetching surgical teams:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch surgical teams');
    }
  },

  /**
   * Get teams by status for a facility
   * @param {string} facilityId - Facility ID
   * @param {string} status - Team status
   * @param {Object} options - Additional options (page, limit)
   * @returns {Promise<Array>} Promise resolving to array of teams
   */
  getTeamsByStatus: async (facilityId, status, options = {}) => {
    try {
      console.log('Fetching teams by status via API:', status, 'for facility:', facilityId);
      
      return await surgicalTeamsService.getAllTeams(facilityId, { status, ...options });
    } catch (error) {
      console.error('Error fetching teams by status:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch teams by status');
    }
  },

  /**
   * Get teams by specialty for a facility
   * @param {string} facilityId - Facility ID
   * @param {string} specialty - Team specialty
   * @param {Object} options - Additional options (page, limit)
   * @returns {Promise<Array>} Promise resolving to array of teams
   */
  getTeamsBySpecialty: async (facilityId, specialty, options = {}) => {
    try {
      console.log('Fetching teams by specialty via API:', specialty, 'for facility:', facilityId);
      
      return await surgicalTeamsService.getAllTeams(facilityId, { specialty, ...options });
    } catch (error) {
      console.error('Error fetching teams by specialty:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch teams by specialty');
    }
  },

  /**
   * Get a single team by ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object|null>} Promise resolving to team data or null
   */
  getTeamById: async (teamId) => {
    try {
      console.log('Fetching team via API:', teamId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/${teamId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const team = response.data.team;
      console.log('Fetched team:', team);
      return team;
    } catch (error) {
      console.error('Error fetching team:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log('Team not found:', teamId);
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch team');
    }
  },

  /**
   * Create a new surgical team
   * @param {Object} teamData - The team data
   * @returns {Promise<string>} Promise resolving to the new team ID
   */
  createTeam: async (teamData) => {
    try {
      console.log('Creating surgical team via API:', teamData);
      const token = await getAuthToken();
      
      // Validate required fields
      if (!teamData.facilityId) {
        throw new Error('Facility ID is required');
      }
      if (!teamData.name) {
        throw new Error('Team name is required');
      }

      const response = await axios.post(
        `${API_BASE}/surgical/surgical-teams`,
        teamData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Created surgical team with ID:', response.data.teamId);
      return response.data.teamId;
    } catch (error) {
      console.error('Error creating surgical team:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create surgical team');
    }
  },

  /**
   * Update an existing surgical team
   * @param {string} teamId - Team ID
   * @param {Object} teamData - Partial team data to update
   * @returns {Promise<void>}
   */
  updateTeam: async (teamId, teamData) => {
    try {
      console.log('Updating surgical team via API:', teamId, teamData);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/surgical/surgical-teams/${teamId}`,
        teamData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated surgical team:', teamId);
    } catch (error) {
      console.error('Error updating surgical team:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update surgical team');
    }
  },

  /**
   * Delete a surgical team
   * @param {string} teamId - Team ID
   * @returns {Promise<void>}
   */
  deleteTeam: async (teamId) => {
    try {
      console.log('Deleting surgical team via API:', teamId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/surgical/surgical-teams/${teamId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Deleted surgical team:', teamId);
    } catch (error) {
      console.error('Error deleting surgical team:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to delete surgical team');
    }
  },

  /**
   * Add member to team
   * @param {string} teamId - Team ID
   * @param {Object} member - Member data
   * @returns {Promise<void>}
   */
  addTeamMember: async (teamId, member) => {
    try {
      console.log('Adding member to team via API:', teamId, member);
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/surgical/surgical-teams/${teamId}/members`,
        member,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Added member to team:', teamId);
    } catch (error) {
      console.error('Error adding team member:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to add team member');
    }
  },

  /**
   * Remove member from team
   * @param {string} teamId - Team ID
   * @param {string} memberId - Member ID
   * @returns {Promise<void>}
   */
  removeTeamMember: async (teamId, memberId) => {
    try {
      console.log('Removing member from team via API:', teamId, memberId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/surgical/surgical-teams/${teamId}/members/${memberId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Removed member from team:', teamId);
    } catch (error) {
      console.error('Error removing team member:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to remove team member');
    }
  },

  /**
   * Update team member
   * @param {string} teamId - Team ID
   * @param {string} memberId - Member ID
   * @param {Object} memberData - Updated member data
   * @returns {Promise<void>}
   */
  updateTeamMember: async (teamId, memberId, memberData) => {
    try {
      console.log('Updating team member via API:', teamId, memberId, memberData);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/surgical/surgical-teams/${teamId}/members/${memberId}`,
        memberData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated team member:', teamId, memberId);
    } catch (error) {
      console.error('Error updating team member:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update team member');
    }
  },

  /**
   * Update team performance metrics
   * @param {string} teamId - Team ID
   * @param {Object} metrics - Performance metrics
   * @returns {Promise<void>}
   */
  updateTeamMetrics: async (teamId, metrics) => {
    try {
      console.log('Updating team metrics via API:', teamId, metrics);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/surgical/surgical-teams/${teamId}/metrics`,
        metrics,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated team metrics:', teamId);
    } catch (error) {
      console.error('Error updating team metrics:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update team metrics');
    }
  },

  // Additional helper methods for enhanced functionality

  /**
   * Get teams with pagination and filtering
   * @param {string} facilityId - Facility ID
   * @param {Object} filters - Filtering options
   * @param {Object} pagination - Pagination options
   * @returns {Promise<Object>} Promise resolving to paginated results
   */
  getTeamsPaginated: async (facilityId, filters = {}, pagination = {}) => {
    try {
      console.log('Fetching paginated teams via API:', facilityId, filters);
      const token = await getAuthToken();
      const { page = 1, limit = 50 } = pagination;
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add filter parameters
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.specialty) {
        params.append('specialty', filters.specialty);
      }
      if (filters.name) {
        params.append('name', filters.name);
      }
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        teams: response.data.teams,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching paginated teams:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Search teams by criteria
   * @param {string} facilityId - Facility ID
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Array of matching teams
   */
  searchTeams: async (facilityId, searchTerm, filters = {}) => {
    try {
      console.log('Searching teams via API:', facilityId, searchTerm);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/facility/${facilityId}/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.teams;
    } catch (error) {
      // If search endpoint doesn't exist, fallback to getting all and filtering
      try {
        const allTeams = await surgicalTeamsService.getAllTeams(facilityId);
        
        if (!searchTerm || searchTerm.trim() === '') {
          return allTeams;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return allTeams.filter(team => 
          (team.name && team.name.toLowerCase().includes(lowerSearchTerm)) ||
          (team.specialty && team.specialty.toLowerCase().includes(lowerSearchTerm)) ||
          (team.description && team.description.toLowerCase().includes(lowerSearchTerm)) ||
          (team.leadSurgeon && team.leadSurgeon.toLowerCase().includes(lowerSearchTerm))
        );
      } catch (fallbackError) {
        console.error('Error searching teams (fallback):', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get team statistics for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Team statistics
   */
  getTeamStats: async (facilityId) => {
    try {
      console.log('Getting team statistics via API:', facilityId);
    
      
      // Get all teams for the facility and calculate stats
      const allTeams = await surgicalTeamsService.getAllTeams(facilityId);
      
      // Calculate statistics from the teams data
      const stats = {
        totalTeams: allTeams.length,
        activeTeams: allTeams.filter(t => t.status === 'active').length,
        inactiveTeams: allTeams.filter(t => t.status === 'inactive').length,
        availableTeams: allTeams.filter(t => t.status === 'available').length,
        busyTeams: allTeams.filter(t => t.status === 'busy').length,
        bySpecialty: allTeams.reduce((acc, t) => {
          const specialty = t.specialty || 'General';
          acc[specialty] = (acc[specialty] || 0) + 1;
          return acc;
        }, {}),
        totalSurgeries: allTeams.reduce((sum, t) => sum + (t.totalSurgeries || 0), 0),
        averageSuccessRate: allTeams.length > 0 
          ? allTeams.reduce((sum, t) => sum + (t.successRate || 0), 0) / allTeams.length 
          : 0,
        totalMembers: allTeams.reduce((sum, t) => sum + (t.members?.length || 0), 0)
      };
      
      return stats;
    } catch (error) {
      console.error('Error getting team stats:', error);
      throw error;
    }
  },

  /**
   * Get available teams for a specialty
   * @param {string} facilityId - Facility ID
   * @param {string} specialty - Specialty type
   * @returns {Promise<Array>} Array of available teams
   */
  getAvailableTeamsForSpecialty: async (facilityId, specialty) => {
    try {
      console.log('Getting available teams for specialty via API:', facilityId, specialty);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/facility/${facilityId}/available?specialty=${specialty}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.availableTeams;
    } catch (error) {
      // If specialty-specific endpoint doesn't exist, get available teams by status
      try {
        return await surgicalTeamsService.getTeamsByStatus(facilityId, 'available');
      } catch (fallbackError) {
        console.error('Error getting available teams (fallback):', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Assign team to a surgical case
   * @param {string} teamId - Team ID
   * @param {Object} assignmentData - Assignment data
   * @returns {Promise<void>}
   */
  assignTeamToCase: async (teamId, assignmentData) => {
    try {
      console.log('Assigning team to case via API:', teamId, assignmentData);
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/surgical/surgical-teams/${teamId}/assign`,
        assignmentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Assigned team to case:', teamId);
    } catch (error) {
      console.error('Error assigning team to case:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Release team from assignment
   * @param {string} teamId - Team ID
   * @returns {Promise<void>}
   */
  releaseTeamFromAssignment: async (teamId) => {
    try {
      console.log('Releasing team from assignment via API:', teamId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/surgical/surgical-teams/${teamId}/assign`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Released team from assignment:', teamId);
    } catch (error) {
      console.error('Error releasing team from assignment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Update team status
   * @param {string} teamId - Team ID
   * @param {string} status - New status
   * @param {string} reason - Reason for status change
   * @returns {Promise<void>}
   */
  updateTeamStatus: async (teamId, status, reason = '') => {
    try {
      console.log('Updating team status via API:', teamId, status);
      
      await surgicalTeamsService.updateTeam(teamId, {
        status: status,
        statusReason: reason,
        statusUpdatedAt: new Date().toISOString()
      });
      
      console.log('Updated team status:', teamId, status);
    } catch (error) {
      console.error('Error updating team status:', error);
      throw error;
    }
  },

  /**
   * Get team member roles
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} Team member roles
   */
  getTeamMemberRoles: async (teamId) => {
    try {
      console.log('Getting team member roles via API:', teamId);
      
      const team = await surgicalTeamsService.getTeamById(teamId);
      
      if (!team) {
        throw new Error('Team not found');
      }
      
      return team.members || [];
    } catch (error) {
      console.error('Error getting team member roles:', error);
      throw error;
    }
  },

  /**
   * Get team schedule
   * @param {string} teamId - Team ID
   * @param {Object} dateRange - Date range for schedule
   * @returns {Promise<Array>} Team schedule
   */
  getTeamSchedule: async (teamId, dateRange = {}) => {
    try {
      console.log('Getting team schedule via API:', teamId, dateRange);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (dateRange.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        params.append('endDate', dateRange.endDate);
      }
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/${teamId}/schedule?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.schedule;
    } catch (error) {
      console.error('Error getting team schedule:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Export team data
   * @param {string} facilityId - Facility ID
   * @param {Object} filters - Export filters
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<string>} Exported data
   */
  exportTeams: async (facilityId, filters = {}, format = 'json') => {
    try {
      console.log('Exporting teams via API:', facilityId, filters, format);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('format', format);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/facility/${facilityId}/export?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (format === 'json') {
        return JSON.stringify(response.data.teams, null, 2);
      } else {
        return response.data.exportData;
      }
    } catch (error) {
      console.error('Error exporting teams:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Get team performance analytics
   * @param {string} teamId - Team ID
   * @param {Object} dateRange - Date range for analytics
   * @returns {Promise<Object>} Team performance analytics
   */
  getTeamPerformanceAnalytics: async (teamId, dateRange = {}) => {
    try {
      console.log('Getting team performance analytics via API:', teamId, dateRange);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (dateRange.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        params.append('endDate', dateRange.endDate);
      }
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-teams/${teamId}/analytics?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.analytics;
    } catch (error) {
      console.error('Error getting team performance analytics:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }
};

export default surgicalTeamsService;
